import React, { useEffect, useState } from "react";
import {
  Button,
  Table,
  message,
  Tag,
  Alert,
  Tabs,
  Tooltip,
  Space,
  Popconfirm,
  Typography,
  Badge,
  Select,
  Popover,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useNavigate, useLocation } from "react-router-dom";
import {
  EditOutlined,
  EyeOutlined,
  EnvironmentOutlined,
  ExclamationCircleOutlined,
  InboxOutlined,
  InboxOutlined as ArchiveOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useBookings } from "../hooks/useBookings";
import { useCompany } from "../hooks/useCompany";
import { useCompanyDetails } from "../hooks/useCompanyDetails";
import { useTranslation } from "react-i18next";
import { css } from "@emotion/css";
import {
  normalizeBookingStatus,
  statusColors,
  BookingStatus,
} from "../utils/bookingStatus";
import styled from "@emotion/styled";
import BookingTableColumnManager, {
  CustomColumn,
  getSavedColumns,
  applyColumnVisibility,
} from "../components/BookingTableColumnManager";
import {
  downloadBookingsListCsv,
  BOOKINGS_CSV_EXPORT_SCOPE_KEYS,
} from "../utils/bookingListCsvExport";

interface BookingRecord {
  id: number;
  reference: string;
  status: string;
  eta?: string | null;
  transport_type: {
    name: string;
    abbreviation: string;
  };
  booking_type?: {
    name: string;
  };
  booking_incoterm: {
    name: string;
  };
  origin_pickup_date: string;
  created_at: string;
  company_name: string;
  rejection_reason?: string | null;
  pickup_request?: {
    status: string;
    cost?: number;
    created_at: string;
    notes?: string;
  }[];
  // Additional fields for container info
  container?: {
    reference: string | null;
    eta: string | null;
    etd: string | null;
    delayed_etd?: string | null;
    eta_customer?: string | null;
    status: string;
    track_and_trace: string | null;
    origin_port?: {
      name: string;
      country: string | null;
    };
    destination_port?: {
      name: string;
      country: string | null;
    };
  };
  // Additional fields for products
  supplier?: {
    name: string;
  };
  // Product totals
  products_summary?: {
    total_cbm: number;
    total_cartons: number;
    total_weight: number;
    total_pieces: number;
    product_count: number;
  };
  products?: Array<{
    name: string;
    ean?: string;
  }>;
  delivery_type?: "storage" | "address";
  delivery_details?: Array<{
    country: string;
    city: string;
    postal_code: string;
    street_name: string;
    house_number: string;
    instructions: string | null;
  }>;
}

// Contact info component to ensure consistent rendering
const ContactInfo = () => {
  const { t } = useTranslation();
  const email = "info@efreightforwarding.com";
  const phone = "+31 (0)85 0607 063";

  // Split the translation string into parts
  const parts = t("companyInfo.contactInfo", {
    email: "{email}",
    phone: "{phone}",
  }).split(/\{(email|phone)\}/);

  return (
    <span>
      {parts.map((part, index) => {
        if (part === "email") {
          return (
            <a key={index} href={`mailto:${email}`}>
              {email}
            </a>
          );
        } else if (part === "phone") {
          return (
            <a key={index} href={`tel:${phone.replace(/\s/g, "")}`}>
              {phone}
            </a>
          );
        } else {
          return <span key={index}>{part}</span>;
        }
      })}
    </span>
  );
};

// Add a CSS class for compact tables
const compactTableStyles = css`
  .ant-table-thead > tr > th {
    padding: 8px 16px;
  }
  .ant-table-tbody > tr > td {
    padding: 6px 16px;
  }
  .ant-table-container {
    overflow-x: auto;
  }
  .ant-table {
    min-width: 1500px;
  }
  .ant-table-cell {
    white-space: nowrap;
  }
  .ant-table-content {
    overflow: auto;
  }
`;

const containerStyles = css`
  padding-right: 24px;
  overflow-x: hidden; // Prevent double scrollbars
  width: 100%;
`;

// Add mobile-friendly styles
const mobileCardStyles = css`
  @media (max-width: 768px) {
    .ant-table {
      display: none;
    }
    .mobile-cards {
      display: block;
    }
  }
  @media (min-width: 769px) {
    .mobile-cards {
      display: none;
    }
  }
`;

const BookingCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  cursor: pointer;

  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
`;

export const Bookings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { selectedCompany } = useCompany();
  const { details, loading: companyLoading } =
    useCompanyDetails(selectedCompany);

  // Get the tab from URL query parameters
  const queryParams = new URLSearchParams(location.search);
  const tabFromUrl = queryParams.get("tab");
  const [activeTab, setActiveTab] = useState<string>(tabFromUrl || "all");
  const [customColumns, setCustomColumns] =
    useState<CustomColumn[]>(getSavedColumns());
  const [csvExportScope, setCsvExportScope] = useState<string>("all");
  const [csvExportPopoverOpen, setCsvExportPopoverOpen] = useState(false);

  const {
    bookings,
    loading: bookingsLoading,
    error,
    archiveBooking,
    refetch,
  } = useBookings(selectedCompany, activeTab === "archived");
  const [localBookings, setLocalBookings] = useState<BookingRecord[]>([]);

  // Function to handle booking archiving
  const handleArchive = async (id: number) => {
    try {
      await archiveBooking(id);
      message.success(t("bookings.archivedSuccess"));
      // Refresh the bookings list
      refetch();
    } catch (err) {
      console.error("Error archiving booking:", err);
      message.error(t("common.error"));
    }
  };

  // Update the URL when the active tab changes
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    navigate(`/bookings${tab === "all" ? "" : `?tab=${tab}`}`, {
      replace: true,
    });
  };

  // Update active tab when URL changes
  useEffect(() => {
    if (
      tabFromUrl &&
      [
        "all",
        "concept",
        "submitted",
        "rejected",
        "approved",
        "waiting_for_goods",
        "goods_received_origin",
        "customs_pending_clearance",
        "customs_cleared",
        "fyco",
        "delivery_planned",
        "out_for_delivery",
        "delivered",
        "added_to_stock",
        "completed",
        "in_transit",
        "cancelled",
        "archived",
      ].includes(tabFromUrl)
    ) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  // Check if company is approved
  const isCompanyApproved = details?.status === "approved";

  // Check for submitted booking in localStorage
  useEffect(() => {
    // First, set localBookings to the fetched bookings
    if (bookings && bookings.length > 0) {
      // Convert bookings to BookingRecord type
      const convertedBookings = bookings.map((booking) => {
        // Create a BookingRecord from the Booking
        const record: BookingRecord = {
          id: booking.id,
          reference: booking.reference,
          status: normalizeBookingStatus(booking.status),
          eta: booking.eta,
          transport_type: booking.transport_type,
          booking_type: booking.booking_type,
          booking_incoterm: booking.booking_incoterm,
          created_at: booking.created_at,
          origin_pickup_date: booking.origin_pickup_date || "",
          company_name: details?.name || "Your Company",
          rejection_reason: booking.rejection_reason,
          pickup_request: booking.pickup_request,
          container: booking.container,
          supplier: booking.supplier,
          products_summary: booking.products_summary,
          products: booking.products,
          delivery_type: booking.delivery_type,
          delivery_details: booking.delivery_details,
        };
        return record;
      });

      setLocalBookings(convertedBookings);

      // Then check if we have a submitted booking in localStorage
      const submittedBookingId = localStorage.getItem("submittedBookingId");
      const submittedBookingStatus = localStorage.getItem(
        "submittedBookingStatus",
      );

      if (submittedBookingId && submittedBookingStatus) {
        // Find the booking in the converted bookings
        const submittedBooking = convertedBookings.find(
          (b) => b.id === parseInt(submittedBookingId),
        );

        if (submittedBooking) {
          // Update the booking's status
          submittedBooking.status = submittedBookingStatus;
          // Update the local bookings state
          setLocalBookings([...convertedBookings]);
          // Show success message
          message.success(t("bookings.submittedMessage"));
          // Clear the localStorage
          localStorage.removeItem("submittedBookingId");
          localStorage.removeItem("submittedBookingStatus");
        }
      }
    }
  }, [bookings, details?.name, t]);

  // Display error if there's an issue loading bookings
  if (error) {
    message.error(`Error loading bookings: ${error}`);
  }

  // Show loading state if either company details or bookings are loading
  const loading = companyLoading || bookingsLoading;

  // Function to handle column changes
  const handleColumnsChange = (newColumns: CustomColumn[]) => {
    setCustomColumns(newColumns);
  };

  // Define all possible table columns
  const getBaseColumns = (): ColumnsType<BookingRecord> => [
    {
      key: "bookingId",
      title: t("bookings.bookingId"),
      render: (_: Record<string, unknown>, record: BookingRecord) =>
        `EFF${record.id}${record.transport_type?.abbreviation || ""}`,
      sorter: (a, b) => a.id - b.id,
    },
    {
      key: "reference",
      title: t("bookings.reference"),
      dataIndex: "reference",
      render: (text: string) => text || "-",
      sorter: (a, b) => (a.reference || "").localeCompare(b.reference || ""),
    },
    {
      key: "status",
      title: t("common.status", "Status"),
      dataIndex: "status",
      render: (status: string, record: BookingRecord) => {
        const normalizedStatus = normalizeBookingStatus(
          status,
        ) as BookingStatus;

        // Ensure the translation is always a string
        const statusText = (() => {
          const translation = t(`status.${normalizedStatus}`);
          return typeof translation === "string"
            ? translation
            : normalizedStatus;
        })();

        return status === "rejected" && record.rejection_reason ? (
          <Tooltip title={record.rejection_reason}>
            <Tag color={statusColors[normalizedStatus] || "default"}>
              {statusText} <ExclamationCircleOutlined />
            </Tag>
          </Tooltip>
        ) : (
          <Tag color={statusColors[normalizedStatus] || "default"}>
            {statusText}
          </Tag>
        );
      },
      sorter: (a, b) => a.status.localeCompare(b.status),
    },
    {
      key: "transportType",
      title: t("bookings.transportType"),
      dataIndex: ["transport_type", "name"],
      sorter: (a, b) =>
        (a.transport_type?.name || "").localeCompare(
          b.transport_type?.name || "",
        ),
    },
    {
      key: "bookingType",
      title: t("bookings.bookingType"),
      dataIndex: ["booking_type", "name"],
      render: (_: Record<string, unknown>, record: BookingRecord) =>
        record.booking_type ? record.booking_type.name : "-",
      sorter: (a, b) =>
        (a.booking_type?.name || "").localeCompare(b.booking_type?.name || ""),
    },
    {
      key: "incoterm",
      title: t("bookings.incoterm"),
      dataIndex: ["booking_incoterm", "name"],
      render: (_: unknown, record: BookingRecord) => {
        if (!record.pickup_request || record.pickup_request.length === 0) {
          return record.booking_incoterm?.name || "-";
        }
        const latestRequest = [...record.pickup_request].sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        )[0];
        let color = "default";
        let badgeStatus:
          | "default"
          | "processing"
          | "success"
          | "error"
          | "warning" = "default";
        switch (latestRequest.status) {
          case "requested":
            color = "blue";
            badgeStatus = "processing";
            break;
          case "pending":
            color = "orange";
            badgeStatus = "warning";
            break;
          case "offer":
            color = "purple";
            badgeStatus = "warning";
            break;
          case "accepted":
            color = "green";
            badgeStatus = "success";
            break;
          case "expired":
            color = "red";
            badgeStatus = "error";
            break;
        }
        return (
          <span>
            {record.booking_incoterm?.name || "-"}{" "}
            <Tooltip
              title={
                <>
                  <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
                    {t("bookings.pickupRequest")}:{" "}
                    <Tag color={color}>
                      {latestRequest.status === "requested"
                        ? t("pickupRequest.statusRequested", "Requested")
                        : latestRequest.status === "pending"
                          ? t("pickupRequest.statusPending", "Pending")
                          : latestRequest.status === "offer"
                            ? t("pickupRequest.statusOffer", "Offer")
                            : latestRequest.status === "accepted"
                              ? t("pickupRequest.statusAccepted", "Accepted")
                              : t("pickupRequest.statusExpired", "Expired")}
                    </Tag>
                  </div>
                  {latestRequest.cost && (
                    <div style={{ marginBottom: "4px" }}>
                      {t("pickupFee")}: €{latestRequest.cost.toFixed(2)}
                    </div>
                  )}
                  {latestRequest.notes && (
                    <div style={{ marginBottom: "4px" }}>
                      {t("notes")}: {latestRequest.notes}
                    </div>
                  )}
                  <div style={{ fontSize: "0.8em", marginTop: "4px" }}>
                    {dayjs(latestRequest.created_at).format("DD/MM/YYYY")}
                  </div>
                </>
              }
            >
              <Badge status={badgeStatus} />
            </Tooltip>
          </span>
        );
      },
      sorter: (a, b) =>
        (a.booking_incoterm?.name || "").localeCompare(
          b.booking_incoterm?.name || "",
        ),
    },
    {
      key: "cargoReadinessDate",
      title: t("bookings.cargoReadinessDate"),
      dataIndex: "origin_pickup_date",
      render: (date: string) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
      sorter: (a, b) => {
        if (!a.origin_pickup_date) return -1;
        if (!b.origin_pickup_date) return 1;
        return (
          dayjs(a.origin_pickup_date).unix() -
          dayjs(b.origin_pickup_date).unix()
        );
      },
    },
    {
      key: "originPort",
      title: t("bookings.containerOriginPort"),
      render: (_: unknown, record: BookingRecord) =>
        record.container?.origin_port
          ? `${record.container.origin_port.name}${
              record.container.origin_port.country
                ? ` (${record.container.origin_port.country})`
                : ""
            }`
          : "-",
      sorter: (a, b) =>
        (a.container?.origin_port?.name || "").localeCompare(
          b.container?.origin_port?.name || "",
        ),
    },
    {
      key: "destinationPort",
      title: t("bookings.containerDestinationPort"),
      render: (_: unknown, record: BookingRecord) =>
        record.container?.destination_port
          ? `${record.container.destination_port.name}${
              record.container.destination_port.country
                ? ` (${record.container.destination_port.country})`
                : ""
            }`
          : "-",
      sorter: (a, b) =>
        (a.container?.destination_port?.name || "").localeCompare(
          b.container?.destination_port?.name || "",
        ),
    },
    {
      key: "supplierName",
      title: t("bookings.supplier"),
      render: (_: unknown, record: BookingRecord) =>
        record.supplier?.name || "-",
      sorter: (a, b) =>
        (a.supplier?.name || "").localeCompare(b.supplier?.name || ""),
    },
    {
      key: "createdAt",
      title: t("bookings.createdAt"),
      dataIndex: "created_at",
      render: (date: string) => dayjs(date).format("DD/MM/YYYY"),
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
    },
    {
      key: "containerReference",
      title: t("bookings.containerReference"),
      render: (_: unknown, record: BookingRecord) =>
        record.container?.reference || "-",
      sorter: (a, b) =>
        (a.container?.reference || "").localeCompare(
          b.container?.reference || "",
        ),
    },
    {
      key: "containerETA",
      title: t("bookings.containerETA"),
      render: (_: unknown, record: BookingRecord) =>
        record.container?.eta
          ? dayjs(record.container.eta).format("DD/MM/YYYY")
          : "-",
      sorter: (a, b) => {
        if (!a.container?.eta) return -1;
        if (!b.container?.eta) return 1;
        return dayjs(a.container.eta).unix() - dayjs(b.container.eta).unix();
      },
    },
    {
      key: "eta",
      title: t("bookings.finalETA", "ETA"),
      render: (_: unknown, record: BookingRecord) => {
        // Priority: booking.eta (override) > container.eta_customer > "-"
        const finalEta = record.eta || record.container?.eta_customer;
        return finalEta ? dayjs(finalEta).format("DD/MM/YYYY") : "-";
      },
      sorter: (a, b) => {
        const aEta = a.eta || a.container?.eta_customer;
        const bEta = b.eta || b.container?.eta_customer;
        if (!aEta) return -1;
        if (!bEta) return 1;
        return dayjs(aEta).unix() - dayjs(bEta).unix();
      },
    },
    {
      key: "containerETD",
      title: t("bookings.containerETD"),
      render: (_: unknown, record: BookingRecord) => {
        const effective =
          record.container?.delayed_etd || record.container?.etd;
        return effective ? dayjs(effective).format("DD/MM/YYYY") : "-";
      },
      sorter: (a, b) => {
        const aEtd = a.container?.delayed_etd || a.container?.etd;
        const bEtd = b.container?.delayed_etd || b.container?.etd;
        if (!aEtd) return -1;
        if (!bEtd) return 1;
        return dayjs(aEtd).unix() - dayjs(bEtd).unix();
      },
    },
    {
      key: "totalProducts",
      title: t("bookings.totalProducts"),
      render: (_: unknown, record: BookingRecord) => {
        const count = record.products_summary?.product_count || 0;
        const productList = record.products
          ?.map(
            (product) =>
              `${product.name}${product.ean ? ` (${product.ean})` : ""}`,
          )
          .join("\n");

        return (
          <Tooltip title={productList} placement="topLeft">
            <span>{count}</span>
          </Tooltip>
        );
      },
      sorter: (a, b) =>
        (a.products_summary?.product_count || 0) -
        (b.products_summary?.product_count || 0),
    },
    {
      key: "totalPieces",
      title: t("bookings.totalPieces"),
      render: (_: unknown, record: BookingRecord) =>
        record.products_summary?.total_pieces || 0,
      sorter: (a, b) =>
        (a.products_summary?.total_pieces || 0) -
        (b.products_summary?.total_pieces || 0),
    },
    {
      key: "totalCartons",
      title: t("bookings.totalCartons"),
      render: (_: unknown, record: BookingRecord) =>
        record.products_summary?.total_cartons || 0,
      sorter: (a, b) =>
        (a.products_summary?.total_cartons || 0) -
        (b.products_summary?.total_cartons || 0),
    },
    {
      key: "totalWeight",
      title: t("bookings.totalWeight"),
      render: (_: unknown, record: BookingRecord) =>
        record.products_summary?.total_weight
          ? `${record.products_summary.total_weight} kg`
          : "-",
      sorter: (a, b) =>
        (a.products_summary?.total_weight || 0) -
        (b.products_summary?.total_weight || 0),
    },
    {
      key: "totalCBM",
      title: t("bookings.totalCBM"),
      render: (_: unknown, record: BookingRecord) =>
        record.products_summary?.total_cbm
          ? `${record.products_summary.total_cbm.toFixed(3)} m³`
          : "-",
      sorter: (a, b) =>
        (a.products_summary?.total_cbm || 0) -
        (b.products_summary?.total_cbm || 0),
    },
    {
      key: "trackAndTrace",
      title: t("bookings.trackAndTrace"),
      render: (_: unknown, record: BookingRecord) =>
        record.container?.track_and_trace ? (
          <Button
            icon={<EnvironmentOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              window.open(record.container?.track_and_trace || "", "_blank");
            }}
          />
        ) : (
          "-"
        ),
      sorter: (a, b) =>
        (a.container?.track_and_trace || "").localeCompare(
          b.container?.track_and_trace || "",
        ),
    },
    {
      key: "deliveryType",
      title: t("bookings.deliveryType"),
      render: (_: unknown, record: BookingRecord) => {
        if (record.delivery_details && record.delivery_details.length > 0) {
          return (
            <Tag color="blue" icon={<EnvironmentOutlined />}>
              {t("bookings.shippedToAddress")}
            </Tag>
          );
        }
        return (
          <Tag color="orange" icon={<InboxOutlined />}>
            {t("bookings.inStorage")}
          </Tag>
        );
      },
      sorter: (a, b) => {
        const aHasDelivery =
          a.delivery_details && a.delivery_details.length > 0;
        const bHasDelivery =
          b.delivery_details && b.delivery_details.length > 0;
        return (aHasDelivery ? 1 : 0) - (bHasDelivery ? 1 : 0);
      },
    },
    {
      key: "actions",
      title: t("common.actions"),
      fixed: "right",
      width: 120,
      render: (_: unknown, record: BookingRecord) => {
        const isEditable =
          record.status === "concept" || record.status === "rejected";
        const canArchive = [
          "delivered",
          "added_to_stock",
          "cancelled",
          "rejected",
          "concept",
          "submitted",
        ].includes(record.status);

        return (
          <Space size="small">
            {isEditable ? (
              <Button
                icon={<EditOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/bookings/${record.id}/edit`);
                }}
                title={t("common.edit")}
                danger={record.status === "rejected"}
              />
            ) : (
              <Button
                icon={<EyeOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/bookings/${record.id}/view`);
                }}
                type="primary"
                title={t("common.view")}
              />
            )}
            {canArchive && (
              <Popconfirm
                title={t(
                  "bookings.confirmArchive",
                  "Are you sure you want to archive this booking?",
                )}
                onConfirm={(e?: React.MouseEvent) => {
                  if (e) {
                    e.stopPropagation();
                  }
                  handleArchive(record.id);
                }}
                okText={t("common.yes", "Yes")}
                cancelText={t("common.no", "No")}
              >
                <Button
                  type="text"
                  icon={<ArchiveOutlined />}
                  danger
                  onClick={(e) => e.stopPropagation()}
                  title={t("bookings.archiveBooking")}
                />
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  // Get the filtered and visible columns based on user preferences
  const getVisibleColumns = () => {
    const baseColumns = getBaseColumns();
    return applyColumnVisibility(baseColumns, customColumns);
  };

  const getFilteredBookings = (status: string) => {
    if (status === "all") {
      return localBookings.filter((booking) => booking.status !== "archived");
    }
    return localBookings.filter((booking) => booking.status === status);
  };

  // Create a reusable function to render tab content
  const renderTabContent = (status: string) => {
    const filteredBookings = getFilteredBookings(status);
    return (
      <>
        <div style={{ width: "100%", overflow: "auto" }}>
          <Table
            columns={getVisibleColumns()}
            dataSource={filteredBookings}
            loading={loading}
            rowKey="id"
            size="small"
            className={compactTableStyles}
            pagination={{ pageSize: 20 }}
            onRow={(record) => ({
              onClick: () =>
                navigate(
                  `/bookings/${record.id}/${
                    record.status === "concept" ? "edit" : "view"
                  }`,
                ),
              style: { cursor: "pointer" },
            })}
            scroll={{ x: true }}
          />
        </div>
        <div className="mobile-cards">
          {filteredBookings.map((booking) => (
            <BookingCard
              key={booking.id}
              onClick={() =>
                navigate(
                  `/bookings/${booking.id}/${
                    booking.status === "concept" ? "edit" : "view"
                  }`,
                )
              }
            >
              <CardHeader>
                <div>
                  <Typography.Text strong>{`EFF${booking.id}${
                    booking.transport_type?.abbreviation || ""
                  }`}</Typography.Text>
                  {booking.reference && (
                    <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                      {booking.reference}
                    </Typography.Text>
                  )}
                </div>
                <Tag
                  color={
                    statusColors[
                      normalizeBookingStatus(booking.status) as BookingStatus
                    ] || "default"
                  }
                >
                  {(() => {
                    const normalizedStatus = normalizeBookingStatus(
                      booking.status,
                    );
                    const translation = t(`status.${normalizedStatus}`);
                    return typeof translation === "string"
                      ? translation
                      : normalizedStatus;
                  })()}
                </Tag>
              </CardHeader>
              <CardContent>
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Typography.Text type="secondary">
                    {t("bookings.transportType")}:
                  </Typography.Text>
                  <Typography.Text>
                    {booking.transport_type?.name || "-"}
                  </Typography.Text>
                </div>
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Typography.Text type="secondary">
                    {t("bookings.incoterm")}:
                  </Typography.Text>
                  <Typography.Text>
                    {booking.booking_incoterm?.name || "-"}
                    {booking.pickup_request &&
                      booking.pickup_request.length > 0 && (
                        <>
                          {" "}
                          <Tooltip
                            title={
                              <>
                                <div
                                  style={{
                                    fontWeight: "bold",
                                    marginBottom: "4px",
                                  }}
                                >
                                  {t("bookings.pickupRequest")}:{" "}
                                  <Tag
                                    color={
                                      booking.pickup_request[0].status ===
                                      "requested"
                                        ? "blue"
                                        : booking.pickup_request[0].status ===
                                            "pending"
                                          ? "orange"
                                          : booking.pickup_request[0].status ===
                                              "offer"
                                            ? "purple"
                                            : booking.pickup_request[0]
                                                  .status === "accepted"
                                              ? "green"
                                              : "red"
                                    }
                                  >
                                    {booking.pickup_request[0].status ===
                                    "requested"
                                      ? t(
                                          "pickupRequest.statusRequested",
                                          "Requested",
                                        )
                                      : booking.pickup_request[0].status ===
                                          "pending"
                                        ? t(
                                            "pickupRequest.statusPending",
                                            "Pending",
                                          )
                                        : booking.pickup_request[0].status ===
                                            "offer"
                                          ? t(
                                              "pickupRequest.statusOffer",
                                              "Offer",
                                            )
                                          : booking.pickup_request[0].status ===
                                              "accepted"
                                            ? t(
                                                "pickupRequest.statusAccepted",
                                                "Accepted",
                                              )
                                            : t(
                                                "pickupRequest.statusExpired",
                                                "Expired",
                                              )}
                                  </Tag>
                                </div>
                                {booking.pickup_request[0].cost && (
                                  <div style={{ marginBottom: "4px" }}>
                                    {t("pickupFee")}: €
                                    {booking.pickup_request[0].cost.toFixed(2)}
                                  </div>
                                )}
                                {booking.pickup_request[0].notes && (
                                  <div style={{ marginBottom: "4px" }}>
                                    {t("notes")}:{" "}
                                    {booking.pickup_request[0].notes}
                                  </div>
                                )}
                                <div
                                  style={{
                                    fontSize: "0.8em",
                                    marginTop: "4px",
                                  }}
                                >
                                  {dayjs(
                                    booking.pickup_request[0].created_at,
                                  ).format("DD/MM/YYYY")}
                                </div>
                              </>
                            }
                          >
                            <Badge
                              status={
                                (booking.pickup_request[0].status ===
                                "requested"
                                  ? "processing"
                                  : booking.pickup_request[0].status ===
                                      "pending"
                                    ? "warning"
                                    : booking.pickup_request[0].status ===
                                        "offer"
                                      ? "warning"
                                      : booking.pickup_request[0].status ===
                                          "accepted"
                                        ? "success"
                                        : "error") as
                                  | "success"
                                  | "processing"
                                  | "default"
                                  | "error"
                                  | "warning"
                              }
                            />
                          </Tooltip>
                        </>
                      )}
                  </Typography.Text>
                </div>
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Typography.Text type="secondary">
                    {t("bookings.cargoReadinessDate")}:
                  </Typography.Text>
                  <Typography.Text>
                    {booking.origin_pickup_date
                      ? dayjs(booking.origin_pickup_date).format("DD/MM/YYYY")
                      : "-"}
                  </Typography.Text>
                </div>
              </CardContent>
              <CardFooter>
                {booking.status === "concept" ||
                booking.status === "rejected" ? (
                  <Button
                    icon={<EditOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/bookings/${booking.id}/edit`);
                    }}
                    danger={booking.status === "rejected"}
                  />
                ) : (
                  <Button
                    icon={<EyeOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/bookings/${booking.id}/view`);
                    }}
                    type="primary"
                  />
                )}
                {[
                  "delivered",
                  "added_to_stock",
                  "cancelled",
                  "rejected",
                  "concept",
                  "submitted",
                ].includes(booking.status) && (
                  <Popconfirm
                    title={t(
                      "bookings.confirmArchive",
                      "Are you sure you want to archive this booking?",
                    )}
                    onConfirm={(e?: React.MouseEvent) => {
                      if (e) {
                        e.stopPropagation();
                      }
                      handleArchive(booking.id);
                    }}
                    okText={t("common.yes", "Yes")}
                    cancelText={t("common.no", "No")}
                  >
                    <Button
                      danger
                      icon={<ArchiveOutlined />}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Popconfirm>
                )}
              </CardFooter>
            </BookingCard>
          ))}
        </div>
      </>
    );
  };

  // Helper function to ensure status translations are strings
  const safeTranslate = (key: string, fallback: string): string => {
    const translation = t(key);
    return typeof translation === "string" ? translation : fallback;
  };

  const handleExportBookingsCsv = () => {
    const rows = getFilteredBookings(csvExportScope);
    if (rows.length === 0) {
      message.warning(t("bookings.csvExportEmpty", "No bookings to export"));
      return;
    }
    downloadBookingsListCsv(
      rows,
      `bookings_${csvExportScope}_${dayjs().format("YYYY-MM-DD")}.csv`,
      t,
    );
    message.success(
      `${t("bookings.csvExportSuccess", "Export complete")} (${rows.length})`,
    );
    setCsvExportPopoverOpen(false);
  };

  // Update the tabs items to use the renderTabContent function
  const tabItems = [
    {
      key: "all",
      label: `${safeTranslate("bookings.all", "All")} (${
        getFilteredBookings("all").length
      })`,
      children: renderTabContent("all"),
    },
    {
      key: "concept",
      label: `${safeTranslate("status.concept", "Concept")} (${
        getFilteredBookings("concept").length
      })`,
      children: renderTabContent("concept"),
    },
    {
      key: "submitted",
      label: `${safeTranslate("status.submitted", "Submitted")} (${
        getFilteredBookings("submitted").length
      })`,
      children: renderTabContent("submitted"),
    },
    {
      key: "approved",
      label: `${safeTranslate("status.approved", "Approved")} (${
        getFilteredBookings("approved").length
      })`,
      children: renderTabContent("approved"),
    },
    {
      key: "rejected",
      label: `${safeTranslate("status.rejected", "Rejected")} (${
        getFilteredBookings("rejected").length
      })`,
      children: renderTabContent("rejected"),
    },
    {
      key: "waiting_for_goods",
      label: `${safeTranslate(
        "status.waiting_for_goods",
        "Waiting for Goods",
      )} (${getFilteredBookings("waiting_for_goods").length})`,
      children: renderTabContent("waiting_for_goods"),
    },
    {
      key: "goods_received_origin",
      label: `${safeTranslate(
        "status.goods_received_origin",
        "Goods Received at Origin",
      )} (${getFilteredBookings("goods_received_origin").length})`,
      children: renderTabContent("goods_received_origin"),
    },
    {
      key: "in_transit",
      label: `${safeTranslate("status.in_transit", "In Transit")} (${
        getFilteredBookings("in_transit").length
      })`,
      children: renderTabContent("in_transit"),
    },
    {
      key: "customs_pending_clearance",
      label: `${safeTranslate(
        "status.customs_pending_clearance",
        "Pending Customs Clearance",
      )} (${getFilteredBookings("customs_pending_clearance").length})`,
      children: renderTabContent("customs_pending_clearance"),
    },
    {
      key: "customs_cleared",
      label: `${safeTranslate("status.customs_cleared", "Customs Cleared")} (${
        getFilteredBookings("customs_cleared").length
      })`,
      children: renderTabContent("customs_cleared"),
    },
    {
      key: "fyco",
      label: `${safeTranslate("status.fyco", "Fyco")} (${
        getFilteredBookings("fyco").length
      })`,
      children: renderTabContent("fyco"),
    },
    {
      key: "delivery_planned",
      label: `${safeTranslate(
        "status.delivery_planned",
        "Delivery Planned",
      )} (${getFilteredBookings("delivery_planned").length})`,
      children: renderTabContent("delivery_planned"),
    },
    {
      key: "out_for_delivery",
      label: `${safeTranslate(
        "status.out_for_delivery",
        "Out for Delivery",
      )} (${getFilteredBookings("out_for_delivery").length})`,
      children: renderTabContent("out_for_delivery"),
    },
    {
      key: "delivered",
      label: `${safeTranslate("status.delivered", "Delivered")} (${
        getFilteredBookings("delivered").length
      })`,
      children: renderTabContent("delivered"),
    },
    {
      key: "added_to_stock",
      label: `${safeTranslate("status.added_to_stock", "Added to Stock")} (${
        getFilteredBookings("added_to_stock").length
      })`,
      children: renderTabContent("added_to_stock"),
    },
    {
      key: "completed",
      label: `${safeTranslate("status.completed", "Completed")} (${
        getFilteredBookings("completed").length
      })`,
      children: renderTabContent("completed"),
    },
    {
      key: "cancelled",
      label: `${safeTranslate("status.cancelled", "Cancelled")} (${
        getFilteredBookings("cancelled").length
      })`,
      children: renderTabContent("cancelled"),
    },
  ];

  return (
    <div className={`${containerStyles} ${mobileCardStyles}`}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <h1>{t("bookings.title", "Bookings")}</h1>
        <Space size={8} align="center">
          <Popover
            trigger="click"
            placement="bottomRight"
            open={csvExportPopoverOpen}
            onOpenChange={setCsvExportPopoverOpen}
            title={t("bookings.exportCsv", "Export CSV")}
            content={
              <Space direction="vertical" size="middle" style={{ width: 260 }}>
                <Select
                  aria-label={t("bookings.csvExportScope", "Bookings to export")}
                  style={{ width: "100%" }}
                  value={csvExportScope}
                  onChange={setCsvExportScope}
                  options={[...BOOKINGS_CSV_EXPORT_SCOPE_KEYS].map((key) => ({
                    value: key,
                    label:
                      key === "all"
                        ? t("bookings.csvExportAllBookings", "All bookings")
                        : `${safeTranslate(`status.${key}`, key)} (${getFilteredBookings(key).length})`,
                  }))}
                />
                <Button
                  type="primary"
                  block
                  icon={<DownloadOutlined />}
                  onClick={handleExportBookingsCsv}
                  disabled={loading}
                >
                  {t("bookings.exportCsvDownload", "Download CSV")}
                </Button>
              </Space>
            }
          >
            <Tooltip title={t("bookings.exportCsv", "Export CSV")}>
              <Button
                icon={<DownloadOutlined />}
                disabled={loading}
              />
            </Tooltip>
          </Popover>
          <BookingTableColumnManager onColumnsChange={handleColumnsChange} />
          <Button
            type="primary"
            onClick={() => navigate("/bookings/new")}
            disabled={
              !isCompanyApproved ||
              companyLoading ||
              details?.status === "under_review"
            }
          >
            {t("bookings.newBooking", "New Booking")}
          </Button>
        </Space>
      </div>

      {!companyLoading && details?.status === "under_review" && (
        <Alert
          type="info"
          message={t(
            "bookings.companyUnderReview",
            "Your company is currently under review. You will be able to create bookings once approved.",
          )}
          style={{ marginBottom: 16 }}
        />
      )}
      {!companyLoading && details?.status === "pending" && (
        <Alert
          type="warning"
          message={
            <span>
              {t(
                "companyInfo.verificationInProgress",
                "We are currently verifying your company information. You cannot make any changes until verification is complete.",
              )}
              <br />
              <br />
              <ContactInfo />
            </span>
          }
          style={{ marginBottom: 16 }}
        />
      )}
      {!companyLoading &&
        !isCompanyApproved &&
        details?.status !== "under_review" &&
        details?.status !== "pending" && (
          <Alert
            type="warning"
            message={
              <span>
                {t(
                  "companyInfo.completeCompanyInfoPrompt",
                  "You need to complete your company information before creating bookings. Go to company info page to enter your company details.",
                )}
                <br />
                <a onClick={() => navigate("/company-info")}>
                  {t("common.companyInfo", "Company Info")}
                </a>
              </span>
            }
            style={{ marginBottom: 16 }}
          />
        )}

      <Tabs activeKey={activeTab} onChange={handleTabChange} items={tabItems} />
    </div>
  );
};
